"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateMetafieldValuesRequestSchema = exports.metafieldValuesSchema = exports.updateMetafieldRequestSchema = exports.createMetafieldRequestSchema = exports.metafieldSchema = void 0;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
const metafields_1 = require("./metafields");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Metafield definitions and values — the C10 wire shapes.                   │
 * └───────────────────────────────────────────────────────────────────────────┘
 */
const keySchema = zod_1.z
    .string()
    .regex(/^[a-z][a-z0-9_]{0,39}$/, 'a key starts with a lower-case letter and holds a-z, 0-9 and _');
exports.metafieldSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `metafield_7b1e4d20` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('metafield'),
    entity: zod_1.z.enum(metafields_1.METAFIELD_ENTITIES),
    name: zod_1.z.string(),
    key: keySchema,
    type: zod_1.z.enum(metafields_1.METAFIELD_TYPES),
    required: zod_1.z.boolean(),
    /** Whether the field appears as a column in list views — the mockup's `list`. */
    inList: zod_1.z.boolean(),
    help: zod_1.z.string().nullable(),
    options: zod_1.z.array(zod_1.z.string()),
    position: zod_1.z.number().int(),
    /**
     * How many rows hold a value for this field.
     *
     * ⚠️ **The delete dialog cannot ask its question without this** — the mockup states *"128
     * feladaton van kitöltve érték. A törléssel ezek is eltűnnek"*, and a confirm dialog that cannot
     * name the number is one nobody can answer.
     */
    usageCount: zod_1.z.number().int().nonnegative(),
    version: zod_1.z.number().int(),
});
/**
 * ⚠️ **`options` is required for `select` and `multi` and forbidden for the rest**, matching the
 * column's own check — and the mockup's *"Adj meg legalább egy választható értéket."* A choice with
 * nothing to choose from is a control that cannot be used; options left on a text field would
 * reappear if somebody changed the type back, silently.
 */
exports.createMetafieldRequestSchema = zod_1.z
    .object({
    entity: zod_1.z.enum(metafields_1.METAFIELD_ENTITIES),
    name: zod_1.z.string().min(1).max(100),
    /** Absent means "derive it from the name" — which is what the form does by default. */
    key: keySchema.optional(),
    type: zod_1.z.enum(metafields_1.METAFIELD_TYPES),
    required: zod_1.z.boolean().optional(),
    inList: zod_1.z.boolean().optional(),
    help: zod_1.z.string().max(500).nullable().optional(),
    options: zod_1.z.array(zod_1.z.string().min(1).max(100)).max(50).optional(),
})
    .refine((value) => value.type === 'select' || value.type === 'multi'
    ? (value.options ?? []).length >= 1
    : (value.options ?? []).length === 0, { path: ['options'], message: 'a choice type needs options, and other types must have none' });
/**
 * ⚠️ **`entity` is ABSENT — a field cannot move between entities.** Its values reference a row of
 * one kind; moving the definition would leave every stored value attached to the wrong sort of
 * thing, and the mockup never offers it either. A field on the wrong entity is a delete and a
 * create, which is visible.
 *
 * ⚠️ **`key` IS present and editable, unlike a project prefix or a client code.** The mockup allows
 * it with a warning — *"A kulcs átírása eltöri a rá hivatkozó integrációkat és exportokat"* — and
 * that warning is the design. Transcribed rather than tightened: locking it would be a decision
 * nobody took.
 */
exports.updateMetafieldRequestSchema = zod_1.z
    .object({
    name: zod_1.z.string().min(1).max(100),
    key: keySchema,
    type: zod_1.z.enum(metafields_1.METAFIELD_TYPES),
    required: zod_1.z.boolean(),
    inList: zod_1.z.boolean(),
    help: zod_1.z.string().max(500).nullable().optional(),
    options: zod_1.z.array(zod_1.z.string().min(1).max(100)).max(50).optional(),
    version: zod_1.z.number().int().nonnegative(),
})
    .refine((value) => value.type === 'select' || value.type === 'multi'
    ? (value.options ?? []).length >= 1
    : (value.options ?? []).length === 0, { path: ['options'], message: 'a choice type needs options, and other types must have none' });
/**
 * The value map for one row, keyed by the metafield's `key`.
 *
 * ⚠️ **Keyed by KEY rather than by id, and that is what the key is for** — it is the API and export
 * name (the mockup's dialog says so), so a request naming ids would be unreadable in a log and
 * unusable from an integration.
 *
 * ⚠️ **`z.unknown()`, deliberately.** Nine types cannot share one Zod shape, and validating them
 * here would put the rules in a second place: `validateValues` in this same package is the one
 * implementation, and the server applies it after parsing.
 */
exports.metafieldValuesSchema = zod_1.z.record(zod_1.z.string(), zod_1.z.unknown());
exports.updateMetafieldValuesRequestSchema = zod_1.z.object({
    values: exports.metafieldValuesSchema,
});
//# sourceMappingURL=contract.js.map